<script module>
  import AppLayout from '$lib/layouts/AppLayout.svelte';
  export const layout = AppLayout;
</script>

<script lang="ts">
  import { page, router } from "@inertiajs/svelte";
  import { toast } from "svelte-sonner";
  import { 
    Save, ArrowLeft, Loader2, Plus, Trash2, GripVertical, 
    Code, Layers, Lock, ChevronDown, ChevronUp, FileCode2, AlertCircle, ExternalLink, Eye
  } from "lucide-svelte";
  import { appStore } from '$lib/stores/appStore';

  import { Button } from "$lib/components/ui/button";
  import { Input } from "$lib/components/ui/input";
  import { Label } from "$lib/components/ui/label";
  import { Textarea } from "$lib/components/ui/textarea";
  import * as Card from "$lib/components/ui/card";
  import { Badge } from "$lib/components/ui/badge";
  import { Separator } from "$lib/components/ui/separator";
  import { Switch } from "$lib/components/ui/switch";
  import { Checkbox } from "$lib/components/ui/checkbox";

  import * as UnderlineTabs from '$lib/components/ui/underline-tabs';

  // 1. 接收 Props 数据（保留 props 引用以维持 Svelte 5 响应式追踪）
  let props = $props<{
    page?: any;
    site_config?: any;
    editing_locale?: string;
    translations?: any;
    current_locale?: string;
  }>();

  const docUrl = $derived(props.site_config?.component_doc_url || import.meta.env?.VITE_COMPONENT_DOC_URL || "https://www.leadnt.com/components");
  const t = $derived(props.translations?.page_editor || {});
  let currentLocale = $state(props.editing_locale || props.page?.locale || props.site_config?.default_locale || "zh");

  // 页面数据本地状态
  let pageData = $state({
    title: "",
    slug: "",
    mode: "components",
    raw_html: "",
    content_blocks: { blocks: [] as any[] },
    seo_metadata: { 
      title: "", 
      description: "", 
      keywords: "",
      hide_header: false,
      hide_footer: false,
      transparent_header: false
    }
  });

  // 当后端返回新的 props.page 或切换语言时，自动更新本地 pageData
  $effect(() => {
    const p = props.page;
    if (p) {
      pageData.title = p.title || "";
      pageData.slug = p.slug || "";
      pageData.mode = p.mode || "components";
      pageData.raw_html = p.raw_html || "";
      pageData.content_blocks = p.content_blocks || { blocks: [] };
      pageData.seo_metadata = {
        title: p.seo_metadata?.title || "",
        description: p.seo_metadata?.description || "",
        keywords: p.seo_metadata?.keywords || "",
        hide_header: !!p.seo_metadata?.hide_header,
        hide_footer: !!p.seo_metadata?.hide_footer,
        transparent_header: !!p.seo_metadata?.transparent_header
      };
    }
  });

  let currentSite = $derived(page.props?.current_site as any);
  // 用于储存每个 Block 正在编辑的 RAW JSON 文本与错误状态
  let rawPropsMap = $state<Record<string, string>>({});
  let jsonErrors = $state<Record<string, string>>({});
  
  // 维护 DOM 引用以实现滚动同步
  let preElements = $state<Record<string, HTMLPreElement>>({});

  // 初始化 rawPropsMap
  $effect(() => {
    if (pageData.content_blocks.blocks) {
      pageData.content_blocks.blocks.forEach((block, idx) => {
        const key = block.id || `block_${idx}`;
        if (rawPropsMap[key] === undefined) {
          const fullObject = {
            component: block.component || "ContactForm",
            props: block.props || {}
          };
          rawPropsMap[key] = JSON.stringify(fullObject, null, 2);
        }
      });
    }
  });

  let processing = $state(false);
  let isDirty = $state(false);
  let activeTab = $state("settings");
  let showRawJson = $state(false);

  $effect(() => {
    appStore.setTitle(t.title || "Page Editor");
  });

  function loadDataForLocale(targetLocale: string) {
    if (targetLocale === currentLocale) return;
    if (isDirty && !confirm(t.btn_switch_dirty || "Unsaved changes exist. Switch anyway?")) return;

    router.visit(window.location.pathname, {
      data: { edit_locale: targetLocale },
      preserveState: true,
      onSuccess: (pageObj) => {
        currentLocale = targetLocale;
        isDirty = false;
        
        const newPage = pageObj.props.page as any;
        if (newPage) {
          pageData.title = newPage.title || "";
          pageData.slug = newPage.slug || "";
          pageData.mode = newPage.mode || "components";
          pageData.raw_html = newPage.raw_html || "";
          pageData.content_blocks = newPage.content_blocks || { blocks: [] };
          pageData.seo_metadata = {
            title: newPage.seo_metadata?.title || "",
            description: newPage.seo_metadata?.description || "",
            keywords: newPage.seo_metadata?.keywords || "",
            hide_header: !!newPage.seo_metadata?.hide_header,
            hide_footer: !!newPage.seo_metadata?.hide_footer,
            transparent_header: !!newPage.seo_metadata?.transparent_header
          };
          rawPropsMap = {};
        }

        toast.success(t.flash?.switch_locale?.replace("%{locale}", targetLocale.toUpperCase()) || `Switched to ${targetLocale.toUpperCase()}`);
      }
    });
  }

  function switchMode(newMode: 'components' | 'code') {
    if (pageData.mode === newMode) return;
    
    if (newMode === 'code' && pageData.content_blocks.blocks?.length > 0) {
      if (!confirm(t.modes?.switch_to_code_confirm || "Switching to code mode will prioritize RAW HTML when saving. Are you sure?")) return;
    } else if (newMode === 'components' && pageData.raw_html.trim() !== '') {
      if (!confirm(t.modes?.switch_to_components_confirm || "Switching to component mode will prioritize component blocks when saving. Are you sure?")) return;
    }

    pageData.mode = newMode;
    isDirty = true;
  }

  function addBlock() {
    const blockId = `block_${Date.now()}`;

    const newBlock = {
      id: blockId,
      component: "",
      props: {}
    };

    rawPropsMap[blockId] = "{\n  \n}";
    pageData.content_blocks.blocks = [...pageData.content_blocks.blocks, newBlock];
    isDirty = true;
  }

  function removeBlock(index: number) {
    const block = pageData.content_blocks.blocks[index];
    if (block?.id) {
      delete rawPropsMap[block.id];
      delete jsonErrors[block.id];
      delete preElements[block.id];
    }
    pageData.content_blocks.blocks = pageData.content_blocks.blocks.filter((_, i) => i !== index);
    isDirty = true;
  }

  function moveBlock(index: number, direction: 'up' | 'down') {
    const blocks = [...pageData.content_blocks.blocks];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= blocks.length) return;

    const [movedBlock] = blocks.splice(index, 1);
    blocks.splice(targetIndex, 0, movedBlock);
    pageData.content_blocks.blocks = blocks;
    isDirty = true;
  }

  function handlePropsJsonInput(blockId: string, index: number, value: string) {
    rawPropsMap[blockId] = value;
    isDirty = true;

    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object') {
        pageData.content_blocks.blocks[index].component = parsed.component || "UnknownComponent";
        pageData.content_blocks.blocks[index].props = parsed.props || {};
      }
      delete jsonErrors[blockId];
    } catch (err: any) {
      jsonErrors[blockId] = err.message || "Invalid JSON syntax";
    }
  }

  function syncScroll(blockKey: string, e: Event) {
    const target = e.currentTarget as HTMLTextAreaElement;
    const pre = preElements[blockKey];
    if (pre) {
      pre.scrollTop = target.scrollTop;
      pre.scrollLeft = target.scrollLeft;
    }
  }

  function highlightJson(jsonStr: string): string {
    if (!jsonStr) return "";
    let html = jsonStr
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    return html.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (match) => {
        let cls = 'text-amber-400';
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'text-sky-300 font-semibold';
          } else {
            cls = 'text-emerald-300';
          }
        } else if (/true|false/.test(match)) {
          cls = 'text-purple-400 font-bold';
        } else if (/null/.test(match)) {
          cls = 'text-rose-400 font-bold';
        }
        return `<span class="${cls}">${match}</span>`;
      }
    );
  }

  function handleSave() {
    if (Object.keys(jsonErrors).length > 0) {
      toast.error(t.flash?.save_error || "Please fix JSON syntax errors before saving.");
      return;
    }

    processing = true;

    const cleanContentBlocks = pageData.mode === 'components' ? pageData.content_blocks : { blocks: [] };
    const cleanRawHtml = pageData.mode === 'code' ? pageData.raw_html : "";

    const payload = {
      title: pageData.title,
      slug: pageData.slug,
      locale: currentLocale,
      mode: pageData.mode,
      content_blocks: cleanContentBlocks,
      raw_html: cleanRawHtml,
      seo_metadata: pageData.seo_metadata
    };

    const isNew = !props.page?.id || props.page.id === 'undefined';
    const method = isNew ? "post" : "put";
    const url = isNew ? "/pages" : `/pages/${props.page.id}`;

    router[method](url, {
      title: pageData.title,
      slug: pageData.slug,
      locale: currentLocale,
      edit_locale: currentLocale,
      full_config: JSON.stringify(payload)
    }, {
      preserveState: false,
      onFinish: () => processing = false,
      onSuccess: () => {
        isDirty = false;
        toast.success(t.flash?.save_success || "Saved successfully!");
      },
      onError: (errs) => {
        console.error("Save error:", errs);
        toast.error(t.flash?.save_error || "Save failed");
      }
    });
  }
</script>

<div class="min-h-screen flex flex-col bg-background text-foreground">
  <!-- 顶部 Header -->
  <header class="h-16 border-b px-6 flex items-center justify-between bg-card/50 backdrop-blur sticky top-0 z-30">
    <div class="flex items-center gap-4">
      <Button variant="ghost" size="icon" onclick={() => router.visit("/pages")}>
        <ArrowLeft class="size-4" />
      </Button>
      <div>
        <h1 class="text-base font-semibold flex items-center gap-2">
          {pageData.title || t.untitled_page || "Untitled Page"}
          {#if isDirty}
            <span class="inline-block w-2 h-2 rounded-full bg-amber-500" title={t.dirty_indicator_tip || "Unsaved changes"}></span>
          {/if}
        </h1>
        <p class="text-xs text-muted-foreground font-mono">/{pageData.slug || 'slug-placeholder'}</p>
      </div>

      <Separator orientation="vertical" class="h-6" />

      <!-- 语言切换 -->
      <div class="flex items-center gap-1 bg-muted/60 p-1 rounded-lg border">
        {#each props.site_config?.supported_locales || [] as loc}
          {@const locCode = typeof loc === 'string' ? loc : loc.code}
          {@const locLabel = typeof loc === 'string' ? loc.toUpperCase() : (loc.label || loc.code.toUpperCase())}
          <button 
            onclick={() => loadDataForLocale(locCode)}
            class="px-2.5 py-1 rounded-md text-xs font-semibold transition-all 
              {currentLocale === locCode ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}"
          >
            { locLabel }
          </button>
        {/each}
      </div>

      {#if currentLocale !== props.site_config?.default_locale}
        <Badge variant="outline" class="gap-1 border-amber-500/30 text-amber-600 bg-amber-50/50">
          <Lock class="size-3" /> {t.non_primary_mode_tip || "Non-primary mode"}
        </Badge>
      {/if}
    </div>

    <div class="flex items-center gap-3">
      {#if pageData.slug}
        {@const previewUrl = `http://${currentSite?.primary_domain}/${pageData.slug}`}
        <a 
          href={previewUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          class="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-medium border rounded-md bg-background hover:bg-accent hover:text-accent-foreground transition-colors shadow-sm"
        >
          <Eye class="size-3.5 text-emerald-500" />
          {t.buttons?.preview || "预览页面"}
        </a>
      {/if}

      <Button variant="outline" size="sm" onclick={() => showRawJson = !showRawJson}>
        <Code class="size-3.5 mr-1.5" />
        {showRawJson ? (t.json_sidebar?.hide || "Hide JSON") : (t.json_sidebar?.show || "Show JSON")}
      </Button>

      <Button onclick={handleSave} disabled={processing || !isDirty} size="sm">
        {#if processing}
          <Loader2 class="size-3.5 mr-1.5 animate-spin" />
          {t.buttons?.saving || "Saving..."}
        {:else}
          <Save class="size-3.5 mr-1.5" />
          {t.buttons?.save || "Save Changes"}
        {/if}
      </Button>
    </div>
  </header>

  <!-- 主内容区域 -->
  <div class="flex-1 flex overflow-hidden">
    <main class="flex-1 overflow-y-auto p-6 space-y-6">
      <UnderlineTabs.Root value={activeTab} onValueChange={(v) => activeTab = v} class="w-full">
        <UnderlineTabs.List class="flex w-full items-center gap-6 border-b border-border pb-0 mb-6 bg-transparent p-0 rounded-none">
          <UnderlineTabs.Trigger 
            value="settings"
            class="pb-3 text-sm font-medium transition-colors hover:text-foreground data-[state=active]:text-primary data-[state=active]:font-semibold"
          >
            {t.tabs?.settings || "Basic & SEO"}
          </UnderlineTabs.Trigger>

          <UnderlineTabs.Trigger 
            value="editor"
            class="pb-3 text-sm font-medium transition-colors hover:text-foreground data-[state=active]:text-primary data-[state=active]:font-semibold"
          >
            {t.tabs?.design || "Page Design"}
          </UnderlineTabs.Trigger>
        </UnderlineTabs.List>

        <!-- TAB 1: 页面设计 -->
        <UnderlineTabs.Content value="editor" class="space-y-6">
          <div class="flex items-center justify-between bg-muted/30 p-4 rounded-xl border">
            <div>
              <h2 class="text-base font-semibold flex items-center gap-2">
                {t.block_manager?.title || "Component Block Management"}
              </h2>
              <p class="text-xs text-muted-foreground mt-0.5">
                {t.block_manager?.subtitle || "Drag and configure blocks. They will be rendered in top-to-bottom order."}
              </p>
            </div>

            <div class="flex items-center gap-3">
              {#if pageData.mode === 'components' && docUrl}
                <a 
                  href={docUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors bg-background px-3 py-1.5 rounded-md border shadow-sm"
                >
                  <ExternalLink class="size-3.5 text-blue-500" />
                  {t.block_manager?.doc_link || "组件说明文档"}
                </a>
              {/if}

              <div class="flex items-center bg-muted p-1 rounded-lg border text-xs">
                <button 
                  onclick={() => switchMode('components')}
                  class="flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-all
                    {pageData.mode === 'components' ? 'bg-background shadow-sm text-primary font-semibold' : 'text-muted-foreground hover:text-foreground'}"
                >
                  <Layers class="size-3.5" /> {t.modes?.components || "Visual Components"}
                </button>
                <button 
                  onclick={() => switchMode('code')}
                  class="flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-all
                    {pageData.mode === 'code' ? 'bg-background shadow-sm text-primary font-semibold' : 'text-muted-foreground hover:text-foreground'}"
                >
                  <FileCode2 class="size-3.5" /> {t.modes?.code || "RAW HTML"}
                </button>
              </div>

              {#if pageData.mode === 'components'}
                <Button size="sm" onclick={addBlock} class="gap-1.5">
                  <Plus class="size-4" /> {t.block_manager?.add_block || "添加区块"}
                </Button>
              {/if}
            </div>
          </div>

          {#if pageData.mode === 'components'}
            {#if pageData.content_blocks.blocks.length === 0}
              <Card.Root class="border-dashed p-12 text-center">
                <Layers class="size-10 mx-auto text-muted-foreground mb-3" />
                <h3 class="text-base font-medium mb-1">{t.block_manager?.empty_title || "暂无组件区块"}</h3>
                <p class="text-sm text-muted-foreground mb-4">{t.block_manager?.empty_subtitle || "点击右上角“添加区块”按钮开始搭建。"}</p>
                <Button variant="outline" size="sm" onclick={addBlock}>
                  <Plus class="size-4 mr-1.5" /> {t.block_manager?.add_block || "添加区块"}
                </Button>
              </Card.Root>
            {:else}
              <div class="space-y-4">
                {#each pageData.content_blocks.blocks as block, idx (block.id || idx)}
                  {@const blockKey = block.id || `block_${idx}`}
                  {@const hasError = !!jsonErrors[blockKey]}
                  {@const rawText = rawPropsMap[blockKey] ?? JSON.stringify({ component: block.component, props: block.props }, null, 2)}
                  {@const displayTitle = t.block_manager?.block_title ? t.block_manager.block_title.replace("%{index}", idx + 1) : `组件 ${idx + 1}`}

                  <Card.Root class="relative group border transition-colors {hasError ? 'border-destructive/60' : ''}">
                    <!-- Card Header -->
                    <Card.Header class="py-1 px-3 flex flex-row items-center justify-between border-b bg-muted/20">
                      <div class="flex items-center gap-2">
                        <div class="cursor-grab text-muted-foreground hover:text-foreground">
                          <GripVertical class="size-3.5" />
                        </div>
                        <span class="text-xs font-semibold text-foreground">
                          {displayTitle}
                        </span>
                      </div>

                      <div class="flex items-center gap-0.5">
                        <Button variant="ghost" size="icon" class="h-7 w-7" disabled={idx === 0} onclick={() => moveBlock(idx, 'up')}>
                          <ChevronUp class="size-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" class="h-7 w-7" disabled={idx === pageData.content_blocks.blocks.length - 1} onclick={() => moveBlock(idx, 'down')}>
                          <ChevronDown class="size-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" class="h-7 w-7 text-destructive hover:bg-destructive/10" onclick={() => removeBlock(idx)}>
                          <Trash2 class="size-3.5" />
                        </Button>
                      </div>
                    </Card.Header>

                    <!-- Card Body -->
                    <Card.Content class="p-3 space-y-2">
                      <div class="flex items-center justify-between">
                        <Label class="text-xs font-medium text-muted-foreground">
                          {t.block_manager?.component_config || "组件配置 (JSON)"}
                        </Label>
                        {#if hasError}
                          <span class="text-xs text-destructive flex items-center gap-1 font-mono">
                            <AlertCircle class="size-3" /> {jsonErrors[blockKey]}
                          </span>
                        {/if}
                      </div>

                      <!-- JSON 代码编辑框与高亮层 -->
                      <div class="relative w-full h-64 rounded-md border border-zinc-800 bg-zinc-950 font-mono text-xs overflow-hidden">
                        <!-- 底层高亮展示 -->
                        <pre 
                          bind:this={preElements[blockKey]}
                          class="absolute inset-0 p-3 m-0 overflow-hidden whitespace-pre break-all leading-relaxed font-mono text-xs text-zinc-100 bg-transparent box-border"
                          aria-hidden="true"
                        ><!-- eslint-disable-next-html -->{@html highlightJson(rawText)}</pre>

                        <!-- 顶层文本输入 -->
                        <textarea
                          rows={10}
                          value={rawText}
                          oninput={(e) => handlePropsJsonInput(blockKey, idx, e.currentTarget.value)}
                          onscroll={(e) => syncScroll(blockKey, e)}
                          class="absolute inset-0 w-full h-full p-3 m-0 bg-transparent text-transparent caret-white overflow-auto outline-none leading-relaxed font-mono whitespace-pre break-all border-none focus:ring-0 resize-none box-border"
                          spellcheck="false"
                        ></textarea>
                      </div>
                    </Card.Content>
                  </Card.Root>
                {/each}
              </div>
            {/if}

          {:else}
            <!-- RAW HTML 代码模式 -->
            <Card.Root>
              <Card.Header>
                <Card.Title>{t.raw_html_editor?.title || "RAW HTML Code Editor"}</Card.Title>
                <Card.Description>{t.raw_html_editor?.subtitle || "Write raw HTML directly."}</Card.Description>
              </Card.Header>
              <Card.Content>
                <Textarea 
                  value={pageData.raw_html} 
                  oninput={(e) => { pageData.raw_html = e.currentTarget.value; isDirty = true; }} 
                  rows={20} 
                  class="font-mono text-xs bg-zinc-950 text-zinc-100 p-4 leading-relaxed" 
                  placeholder={t.raw_html_editor?.placeholder || "<section>...</section>"} 
                />
              </Card.Content>
            </Card.Root>
          {/if}
        </UnderlineTabs.Content>

        <!-- TAB 2: 基本信息与 SEO -->
        <UnderlineTabs.Content value="settings" class="space-y-6">
          <Card.Root>
            <Card.Header>
              <Card.Title>{t.settings?.basic_title || "Basic Settings"}</Card.Title>
            </Card.Header>
            <Card.Content class="space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-2">
                  <Label>{t.settings?.label_page_title || "Page Title"} ({currentLocale.toUpperCase()})</Label>
                  <Input value={pageData.title} oninput={(e) => { pageData.title = e.currentTarget.value; isDirty = true; }} />
                </div>
                <div class="space-y-2">
                  <Label>{t.settings?.label_slug || "Slug"}</Label>
                  <Input value={pageData.slug} disabled={currentLocale !== props.site_config?.default_locale} oninput={(e) => { pageData.slug = e.currentTarget.value; isDirty = true; }} />
                </div>
              </div>
            </Card.Content>
          </Card.Root>

          <Card.Root>
            <Card.Header>
              <Card.Title>{t.settings?.seo_title || "SEO Settings"}</Card.Title>
            </Card.Header>
            <Card.Content class="space-y-4">
              <div class="space-y-2">
                <Label>{t.settings?.label_seo_title || "SEO Title"}</Label>
                <Input value={pageData.seo_metadata.title || ''} oninput={(e) => { pageData.seo_metadata.title = e.currentTarget.value; isDirty = true; }} />
              </div>
              <div class="space-y-2">
                <Label>{t.settings?.label_seo_keywords || "SEO Keywords"}</Label>
                <Input value={pageData.seo_metadata.keywords || ''} oninput={(e) => { pageData.seo_metadata.keywords = e.currentTarget.value; isDirty = true; }} placeholder={t.settings?.placeholder_seo_keywords || "Comma separated"} />
              </div>
              <div class="space-y-2">
                <Label>{t.settings?.label_seo_description || "SEO Description"}</Label>
                <Textarea value={pageData.seo_metadata.description || ''} oninput={(e) => { pageData.seo_metadata.description = e.currentTarget.value; isDirty = true; }} rows={3} />
              </div>
            </Card.Content>
          </Card.Root>

          <!-- 页面布局与布局显示开关卡片 -->
          <Card.Root>
            <Card.Header>
              <Card.Title>{t.settings?.layout_title || "Page Layout Options"}</Card.Title>
              <Card.Description>{t.settings?.layout_subtitle || "Configure header and footer display settings for this page."}</Card.Description>
            </Card.Header>
            <Card.Content class="space-y-6">
              <!-- 1. 隐藏 Header -->
              <div class="flex items-center justify-between space-x-2 py-1">
                <div class="space-y-0.5">
                  <Label class="text-sm font-medium">{t.settings?.label_hide_header || "Hide Header"}</Label>
                  <p class="text-xs text-muted-foreground">{t.settings?.desc_hide_header || "Hide the site navigation header on this page."}</p>
                </div>
                <Checkbox 
                  bind:checked={pageData.seo_metadata.hide_header} 
                  onCheckedChange={() => isDirty = true} 
                />
              </div>

              <Separator />

              <!-- 2. 透明 Header 开关 -->
              <div class="flex items-center justify-between space-x-2">
                <div class="space-y-0.5">
                  <Label class="text-sm font-medium">{t.settings?.label_transparent_header || "Transparent Header"}</Label>
                  <p class="text-xs text-muted-foreground">{t.settings?.desc_transparent_header || "Make the header floating and transparent."}</p>
                </div>
                <Checkbox 
                  bind:checked={pageData.seo_metadata.transparent_header} 
                  onCheckedChange={() => isDirty = true} 
                />
              </div>

              <Separator />

              <!-- 3. 隐藏 Footer 开关 -->
              <div class="flex items-center justify-between space-x-2">
                <div class="space-y-0.5">
                  <Label class="text-sm font-medium">{t.settings?.label_hide_footer || "Hide Footer"}</Label>
                  <p class="text-xs text-muted-foreground">{t.settings?.desc_hide_footer || "Hide the site footer section on this page."}</p>
                </div>
                <Checkbox 
                  bind:checked={pageData.seo_metadata.hide_footer} 
                  onCheckedChange={() => isDirty = true} 
                />
              </div>
            </Card.Content>
          </Card.Root>
        </UnderlineTabs.Content>
      </UnderlineTabs.Root>
    </main>

    <!-- JSON 查看侧栏 -->
    {#if showRawJson}
      <aside class="w-96 border-l bg-zinc-950 text-zinc-300 font-mono text-xs flex flex-col">
        <div class="h-10 border-b border-zinc-800 px-4 flex items-center justify-between bg-zinc-900/50">
          <span class="font-bold flex items-center gap-1.5 text-zinc-400">
            <Code class="size-3.5 text-blue-400" /> {t.json_sidebar?.payload_title || "RAW Payload"}
          </span>
          <span class="text-[10px] text-zinc-500">{t.json_sidebar?.current_mode || "Mode"}: {pageData.mode}</span>
        </div>
        <textarea 
          readonly 
          class="flex-1 bg-transparent p-4 resize-none outline-none text-blue-100/80 leading-relaxed font-mono"
          value={JSON.stringify({
            ...pageData,
            content_blocks: pageData.mode === 'components' ? pageData.content_blocks : { blocks: [] },
            raw_html: pageData.mode === 'code' ? pageData.raw_html : ""
          }, null, 2)}
        ></textarea>
      </aside>
    {/if}
  </div>
</div>